import React, { useCallback, useEffect } from "react";
import { StyleSheet, View } from "react-native";
import { useFocusEffect, useNavigation, useRoute } from "@react-navigation/native";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { useAuth } from "../hooks/useAuth";
import type { RootStackParamList } from "../navigation/navigation.types";
import WebSocketNavigator from "../navigation/WebSocketNavigator";
import { useChatBar, ConversationType } from "../contexts/ChatBarContext";
import returnToUtils from "../utils/returnTo.utils";
import { UserRoleUI, isBelowMinRole } from "../contexts/AuthContext";
import { useAppConfig } from "../AppConfigProvider";
import Loader from "../components/common/status/Loader/Loader";
import { getDefaultAuthenticatedRoute } from "../utils/authRoutes.utils";

type WithAuthOptions = {
  minRequiredRole?: UserRoleUI;
  redirectToIfNoPermission?: keyof RootStackParamList;
};

const withAuth = (WrappedComponent, options: WithAuthOptions = {}) => {
  const WithAuthComponent = (props) => {
    const { isCompanionMode, isLMS, isLTI, ltiLaunchUrl } = useAppConfig();
    const {
      isAuthenticated,
      isEmailVerified,
      otpVerifiedForSession,
      userInfo,
      meData,
      isAuthReady,
      isPermissionsReady,
      isUpdating,
    } = useAuth();
    const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
    const route = useRoute();
    const name = route.name;
    const params = route.params;
    const { mapRouteToContext, setActiveContextAndObjectId, setContext, setConversationType } = useChatBar();
    const { minRequiredRole, redirectToIfNoPermission } = options;

    const isVerified = isEmailVerified && otpVerifiedForSession;

    WebSocketNavigator();

    useFocusEffect(
      useCallback(() => {
        const activeContext = mapRouteToContext(name as keyof RootStackParamList, params);

        let objectId = null;
        switch (activeContext) {
          case "track":
            objectId = (params as RootStackParamList["Lesson Chat"]).loId;
            break;
          case "assignment":
          case "ai_tutor":
            objectId = (params as RootStackParamList["Resource"]).resource_id;
            break;
          case "onboarding":
          case "general":
          default:
            objectId = null;
            break;
        }

        let contentType = null;
        switch (activeContext) {
          case "track":
            contentType = ConversationType.LEARNING;
            break;
          case "assignment":
          case "ai_tutor":
            contentType = ConversationType.SCHOOL;
            break;
          case "onboarding":
            contentType = ConversationType.ONBOARD;
            break;
          case "general":
          default:
            contentType = ConversationType.GENERAL_CONVERSATION;
            break;
        }

        setConversationType(contentType);
        setActiveContextAndObjectId({ context: activeContext, objectId });
        setContext({ context: activeContext, params: params as { [key: string]: string } });
      }, [mapRouteToContext, name, params, setActiveContextAndObjectId, setContext, setConversationType])
    );

    useEffect(() => {
      if (!isAuthReady || !isPermissionsReady || !isAuthenticated || !isVerified || !minRequiredRole) {
        return;
      }

      if (isBelowMinRole(userInfo.userRole, minRequiredRole)) {
        const redirect =
          redirectToIfNoPermission ?? getDefaultAuthenticatedRoute({ userRole: userInfo.userRole, isCompanionMode });
        navigation.reset({
          index: 0,
          routes: [{ name: redirect as keyof RootStackParamList, params }],
        });
      }
    }, [
      isAuthReady,
      isPermissionsReady,
      isAuthenticated,
      isVerified,
      minRequiredRole,
      userInfo.userRole,
      isCompanionMode,
      redirectToIfNoPermission,
      navigation,
      params,
    ]);

    useFocusEffect(
      useCallback(() => {
        if (!isAuthReady || isUpdating) {
          return;
        }

        if (!isAuthenticated) {
          const currentUrl = window.location.pathname + window.location.search;
          returnToUtils.storeReturnToUrl(currentUrl);
          if (isLTI && ltiLaunchUrl) {
            window.location.href = ltiLaunchUrl;
            return;
          }
          navigation.reset({
            index: 0,
            routes: [{ name: "Login" }],
          });
          return;
        }

        if (!isVerified) {
          const currentUrl = window.location.pathname + window.location.search;
          returnToUtils.storeReturnToUrl(currentUrl);
          navigation.reset({
            index: 0,
            routes: [{ name: "Verify Email" }],
          });
          return;
        }

        if (isCompanionMode && name === "Onboarding") {
          navigation.reset({
            index: 0,
            routes: [{ name: "Companion Home" }],
          });
        }
      }, [isAuthReady, isAuthenticated, isVerified, isCompanionMode, isLTI, isUpdating, ltiLaunchUrl, name, navigation])
    );

    if (!isAuthReady || isUpdating) {
      return (
        <View style={styles.loaderContainer}>
          <Loader />
        </View>
      );
    }

    if (!isAuthenticated || !meData?.me || !isVerified) {
      return (
        <View style={styles.loaderContainer}>
          <Loader />
        </View>
      );
    }

    return <WrappedComponent {...props} />;
  };

  return WithAuthComponent;
};

const styles = StyleSheet.create({
  loaderContainer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },
});

export default withAuth;
